import React from "react"
import { graphql, Link } from "gatsby"
import { RentalLayoutSingleColumn } from "@organic-return/foundation-react/src/components/Listing"
import { RentalLayoutAlt } from "@organic-return/foundation-react/src/components/Listing"
import MainPage from "./MainPage"
import { useSiteContext } from "../components/common/SiteContext"
import { EWsrvTransform } from "@organic-return/foundation-react/src/lib"

import axios from "axios"
import { getRecaptchaToken, getSegmentAnonymousId, registerSegmentActivity } from "../lib/utils"

export const query = graphql`
  query($nodeID: String!) {
    rental(id: { eq: $nodeID }) {
      id
      baths
      bathsHalf
      address {
        city
        state
        street1
        street2
        postal
        text
      }
      beds
      sleeps
      subdivision
      type
      defaultImage {
        dimensions
        url
      }
      price
      priceLow
      priceHigh
      priceSuffix
      path
      flags
      features
      description
      descriptionHTML
      images {
        url
        dimensions
        caption
      }
      bookings {
        endDate
        startDate
      }
      rates {
        price
        startDate
        endDate
        type
      }
      location {
        lat
        lng
      }
      details {
        name
        details {
          value
          label
        }
      }
      agent {
        mlsID
        first
        last
        fullName
        email
        company
        category
        officeName
        phoneOffice
        photos {
          url
        }
        social {
          channel
          url
        }
        path
        phone
        phoneMobile
      }
      office {
        mlsID
        fullName
        email
        phone
        photos {
          url
        }
        address {
          street1
          street2
          city
          state
          postal
          text
        }
      }
      similar {
        id
        baths
        address {
          city
          state
          text
          street1
          street2
          postal
        }
        price
        priceLow
        priceHigh
        priceSuffix
        flags
        features
        beds
        sleeps
        subdivision
        type
        defaultImage {
          dimensions
          url
        }
        path
      }
      damageSecurityDeposit
      cleaningFee
      winterComments
      ownerPolicies
      checkInTime
      checkOutTime
      additionalLodgingFees
      officeName
      tours {
        url
      }
      videos {
        url
      }
      sEOTitle
      sEODescription
    }
  }
`

const RentalPage = ({ pageContext, data, location }: any) => {
  const siteContext = useSiteContext()
  
  const Layout = siteContext.rentalLayout || ((
    siteContext.rentalLayoutProps &&
    siteContext.rentalLayoutProps.className &&
    siteContext.rentalLayoutProps.className.indexOf("useAltLayout") > -1)
    ? RentalLayoutAlt
    : RentalLayoutSingleColumn)

  const requestInformationRentalClick = async (formInput: any) => {
    const recaptchaToken = await getRecaptchaToken("request_information_rental");
    await axios({
      url: process.env.LEAD_CREATE_API_ENDPOINT,
      method: "POST",
      data: {
        recaptchaToken,
        formValues: formInput,
        propertyInfo: {
          createdFrom: `Request Information Rental Listing Page Form`,
          listingId: data.rental.mlsID,
          listingAddress: data.rental.address.text,
          listingPrice: data.rental.price,
          listingAgentMlsId: data.rental.agent && data.rental.agent[0] ? data.rental.agent[0].mlsID : null,
          listingType: data.rental.type
        },
        target: "Rental",
        segmentAnonymousId: getSegmentAnonymousId(),
      },
    })
    registerSegmentActivity(`Request Information Rental Listing Page Form Submission`, formInput)
  };

  if (siteContext.rentalLayoutProps && siteContext.rentalLayoutProps!.formProps) {
    siteContext.rentalLayoutProps!.formProps!.clickHandler = requestInformationRentalClick;
  }
  if (siteContext.rentalLayoutProps && siteContext.rentalLayoutProps!.contactUsFormProps) {
    siteContext.rentalLayoutProps!.contactUsFormProps!.clickHandler = requestInformationRentalClick;
  }

  data.rental.office = data.rental.office ?? [];
  data.rental.agent = data.rental.agent ?? [];

  var pageTitle = data.rental.sEOTitle;
  var pageDescription = data.rental.sEODescription ?? "";

  return (
    <MainPage
      seoProps={{
        title: pageTitle,
        description: pageDescription,
        ogImage: data.rental.defaultImage?.url,
        ogType: "place",
        location: location,
      }}
      headerProps={{ ...siteContext.headerProps, gradient: false }}
      className="rental-page"
    >
      <Layout
        className="max-w-auto mx-auto md:mt-28"
        listing={data.rental}
        title={data.rental.address.text}
        galleryProps={{
          preset: EWsrvTransform.cover16x9,
          width: 1600,
          height: 900,
          className: "max-w-screen-xl mx-auto",
        }}
        similar={data.rental.similar}
        rowClassName="max-w-screen-xl mx-auto"
        linkFunc={props => {
          return (
            <Link
              to={props.href ?? ""}
              className={`hide-disabled text-inherit ${props.className}`}
              key={props.key}
              title={props.title}
            >
              <>{props.children}</>
            </Link>
          )
        }}
        agents={data.rental.agent}
        {...siteContext.rentalLayoutProps}
      />
    </MainPage>
  )
}

export default RentalPage
